import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import Logo from './Logo';
import FooterLogo from './FooterLogo';
import { colors, gradients, shadows, fontSize, fontWeight, spacing, buttonDefaults } from '../style/tokens';
import { useTheme } from 'styled-components';
import { headerBg, headerFg, headerHoverBg } from '../style/header';

// ── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: var(--sfondo, ${colors.bg});
  font-family: 'Outfit', sans-serif;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 26px;
  min-height: 82px;
  background: ${headerBg};
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  position: relative;
`;

const LogoCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const BackButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1.5px solid ${colors.border};
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  @media (hover: hover) { &:hover { background: ${headerHoverBg}; } }
  &::before {
    content: '';
    display: block;
    width: 9px;
    height: 9px;
    border-top: 2px solid ${headerFg};
    border-left: 2px solid ${headerFg};
    transform: rotate(-45deg) translate(2px, 0);
  }
`;

const Spacer = styled.span`width: 42px;`;

// ── Title block ───────────────────────────────────────────────────────────────

const TitleBlock = styled.div`
  background: transparent;
  padding: 22px 24px 18px;
  margin-bottom: 0;
`;

const PageTitle = styled.h1`
  font-size: clamp(24px, 7vw, 30px);
  font-weight: ${fontWeight.extrabold};
  color: ${colors.text};
  line-height: 1.2;
  margin-bottom: ${spacing.sm};
`;

const PageSubtitle = styled.p`
  font-size: ${fontSize.md};
  color: ${colors.muted};
  line-height: 1.6;
`;

// ── Scrollable body ───────────────────────────────────────────────────────────

const Body = styled.div`

  padding: 16px 14px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// ── Info block ────────────────────────────────────────────────────────────────

const InfoBlock = styled.div`
  background: ${colors.bg};
  border: 1.5px solid ${p => p.theme.coloreTitolo};
  border-radius: 14px;
  padding: 16px 18px;
`;

const InfoBlockLabel = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.coloreTitolo};
  margin-bottom: ${spacing.md};
`;

const ConditionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConditionItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: ${fontSize.md};
  line-height: 1.5;
`;

const CondKey = styled.span`
  font-weight: 700;
  color: ${colors.text};
  min-width: 90px;
  flex-shrink: 0;
`;

const CondVal = styled.span`
  color: ${colors.muted};
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoListItem = styled.li`
  font-size: ${fontSize.md};
  color: ${colors.text};
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${p => p.theme.gradient1};
    flex-shrink: 0;
    margin-top: 5px;
  }
`;

const InfoNote = styled.p`
  font-size: ${fontSize.xs};
  color: #aaa;
  line-height: 1.55;
  margin-top: 10px;
`;

// ── Steps ─────────────────────────────────────────────────────────────────────

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Step = styled.div`
  background: ${p => p.theme.coloreBottoneInattivo || p.theme.gradient1};
  border-radius: 14px;
  padding: 18px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StepIcon = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  svg { width: 60px; height: 60px; }
`;

const StepBody = styled.div`
  flex: 1;
`;

const StepTitle = styled.h2`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  color: #fff;
  margin-bottom: ${spacing.xs};
`;

const StepText = styled.p`
  font-size: ${fontSize.md};
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
`;

// ── Actions ───────────────────────────────────────────────────────────────────

const FormActions = styled.div`
  padding: 20px 14px 24px;
`;

const BtnNext = styled.button`
  display: block;
  width: 100%;
  padding: 18px 0;
  border-radius: 50px;
  border: none;
  background: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  color: #fff;
  font-size: 17px;
  font-weight: ${fontWeight.bold};
  font-family: 'Outfit', sans-serif;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; } }
  &:active { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; transform: scale(0.98); }
`;


// ── Props / Component ─────────────────────────────────────────────────────────

interface InstructionsPageProps {
  /** Omitted when no enabled page precedes this one — the back button is hidden. */
  onBack?: () => void;
  onNext: () => void;
}

const InstructionsPage: React.FC<InstructionsPageProps> = ({ onBack, onNext }) => {
  const intl = useIntl();
  return (
    <Page>
      <TopBar>
        {onBack ? <BackButton onClick={onBack} aria-label="Back" /> : <Spacer />}
        <LogoCenter><Logo /></LogoCenter>
        <Spacer />
      </TopBar>

      <TitleBlock>
        <PageTitle>{intl.formatMessage({ id: 'instructions.title' })}</PageTitle>
        <PageSubtitle>{intl.formatMessage({ id: 'instructions.subtitle' })}</PageSubtitle>
      </TitleBlock>

      <Body>
        <InfoBlock>
          <InfoBlockLabel>{intl.formatMessage({ id: 'instructions.conditionsTitle' })}</InfoBlockLabel>
          <ConditionList>
            <ConditionItem>
              <CondKey>{intl.formatMessage({ id: 'instructions.lighting' })}</CondKey>
              <CondVal>{intl.formatMessage({ id: 'instructions.lightingValue' })}</CondVal>
            </ConditionItem>
            <ConditionItem>
              <CondKey>{intl.formatMessage({ id: 'instructions.distance' })}</CondKey>
              <CondVal>{intl.formatMessage({ id: 'instructions.distanceValue' })}</CondVal>
            </ConditionItem>
            <ConditionItem>
              <CondKey>{intl.formatMessage({ id: 'instructions.stillness' })}</CondKey>
              <CondVal>{intl.formatMessage({ id: 'instructions.stillnessValue' })}</CondVal>
            </ConditionItem>
            <ConditionItem>
              <CondKey>{intl.formatMessage({ id: 'instructions.accessories' })}</CondKey>
              <CondVal>{intl.formatMessage({ id: 'instructions.accessoriesValue' })}</CondVal>
            </ConditionItem>
          </ConditionList>
        </InfoBlock>

        {/* <Steps>
          <Step>
            <StepIcon>
              <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="18" width="44" height="30" rx="6" fill="white" opacity=".95"/>
                <circle cx="30" cy="33" r="10" stroke="#5fa812" strokeWidth="3" fill="none"/>
                <circle cx="30" cy="33" r="4.5" fill="#5fa812" opacity=".7"/>
                <rect x="22" y="12" width="16" height="8" rx="3" fill="white" opacity=".95"/>
                <circle cx="44" cy="24" r="2.5" fill="#5fa812" opacity=".8"/>
              </svg>
            </StepIcon>
            <StepBody>
              <StepTitle>{intl.formatMessage({ id: 'instructions.step1Title' })}</StepTitle>
              <StepText>{intl.formatMessage({ id: 'instructions.step1Text' })}</StepText>
            </StepBody>
          </Step>

          <Step>
            <StepIcon>
              <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="22" r="11" fill="white" opacity=".95"/>
                <path d="M18 48 Q30 38 42 48" stroke="white" strokeWidth="3.5" strokeLinecap="round" fill="none" opacity=".95"/>
                <path d="M14 33 Q18 28 22 33 Q26 38 30 33 Q34 28 38 33 Q42 38 46 33"
                      stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity=".85"/>
              </svg>
            </StepIcon>
            <StepBody>
              <StepTitle>{intl.formatMessage({ id: 'instructions.step2Title' })}</StepTitle>
              <StepText>{intl.formatMessage({ id: 'instructions.step2Text' })}</StepText>
            </StepBody>
          </Step>

          <Step>
            <StepIcon>
              <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="13" y="10" width="34" height="42" rx="5" fill="white" opacity=".95"/>
                <rect x="20" y="20" width="20" height="2.5" rx="1.5" fill="#5fa812" opacity=".6"/>
                <rect x="20" y="27" width="14" height="2.5" rx="1.5" fill="#5fa812" opacity=".4"/>
                <circle cx="30" cy="39" r="8" fill="#5fa812" opacity=".2"/>
                <polyline points="25,39 28.5,43 35,35"
                          stroke="#5fa812" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </StepIcon>
            <StepBody>
              <StepTitle>{intl.formatMessage({ id: 'instructions.step3Title' })}</StepTitle>
              <StepText>{intl.formatMessage({ id: 'instructions.step3Text' })}</StepText>
            </StepBody>
          </Step>
        </Steps> */}

        {/* <InfoBlock>
          <InfoBlockLabel>{intl.formatMessage({ id: 'instructions.analysesTitle' })}</InfoBlockLabel>
          <InfoList>
            <InfoListItem>{intl.formatMessage({ id: 'instructions.analysesItem1' })}</InfoListItem>
            <InfoListItem>{intl.formatMessage({ id: 'instructions.analysesItem2' })}</InfoListItem>
            <InfoListItem>{intl.formatMessage({ id: 'instructions.analysesItem3' })}</InfoListItem>
          </InfoList>
          <InfoNote>{intl.formatMessage({ id: 'instructions.privacyNote' })}</InfoNote>
        </InfoBlock> */}
      </Body>

      <FormActions>
        <BtnNext type="button" onClick={onNext}>{intl.formatMessage({ id: 'button.next' })}</BtnNext>
      </FormActions>

      <FooterLogo />

    </Page>
  );
};

export default InstructionsPage;
